// Communication preferences: the types both front doors (Command Center and Settings →
// Notifications) share, the labels the practice reads, and the small pure helpers the card needs.
// Zero imports on purpose: exercised by a plain `node:test` run without the bundler tsconfig.

export type EngagementMode = 'DO_IT_FOR_ME' | 'KEEP_ME_INFORMED' | 'HIGH_TOUCH';

export interface AlwaysAskFloor {
  key: string;
  label: string;
}

/** One record per (practice, agent) as the API returns it. */
export interface CommunicationPreference {
  practiceId: string;
  agentType: string;
  /** Persona and role names from agent-suite's registry (the role key when it cannot be read). */
  personaName: string;
  displayName: string;
  /** The one remaining control: does this agent email the owner. */
  emailEnabled: boolean;
  /** Set from the Command Center: the practice's own settings cannot change `emailEnabled`. */
  emailLockedByAgency: boolean;
  engagementMode: EngagementMode | null;
  supportsEngagementMode: boolean;
  alwaysAsk: readonly AlwaysAskFloor[];
  isDefault: boolean;
  updatedAt: string | null;
}

export interface CommunicationPreferencePatch {
  emailEnabled?: boolean;
  engagementMode?: EngagementMode | null;
  /** Command Center only; the practice door refuses it. */
  emailLockedByAgency?: boolean;
}

export const ENGAGEMENT_OPTIONS: ReadonlyArray<{
  value: EngagementMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'DO_IT_FOR_ME',
    label: 'Do it for me',
    hint: 'Routine calls are made and noted on the thread; you hear about the big ones.',
  },
  {
    value: 'KEEP_ME_INFORMED',
    label: 'Keep me informed',
    hint: 'Routine assumptions are parked for your review; decisions wait for you.',
  },
  {
    value: 'HIGH_TOUCH',
    label: 'High touch',
    hint: 'Every routine call waits for your approval before work continues.',
  },
];

/** The mode the practice is effectively on when it has not chosen one. Mirrors the agent default. */
export const DEFAULT_ENGAGEMENT_MODE: EngagementMode = 'KEEP_ME_INFORMED';

// ─── Diffing the form against the record ─────────────────────────────────────

export interface CommsFormState {
  emailEnabled: boolean;
  emailLockedByAgency: boolean;
  engagementMode: EngagementMode | null;
}

export function formFromPreference(pref: CommunicationPreference): CommsFormState {
  return {
    emailEnabled: pref.emailEnabled,
    emailLockedByAgency: pref.emailLockedByAgency,
    engagementMode: pref.engagementMode,
  };
}

/** Only what changed, so a save from one door never overwrites a field the other door just set. */
export function diffPreference(
  before: CommunicationPreference,
  form: CommsFormState,
): CommunicationPreferencePatch {
  const patch: CommunicationPreferencePatch = {};
  if (before.emailEnabled !== form.emailEnabled) patch.emailEnabled = form.emailEnabled;
  if (before.engagementMode !== form.engagementMode) patch.engagementMode = form.engagementMode;
  if (before.emailLockedByAgency !== form.emailLockedByAgency) {
    patch.emailLockedByAgency = form.emailLockedByAgency;
  }
  return patch;
}
